import { useMemo, useContext } from "react"
import { useQuery } from "@tanstack/react-query"
import { useDispatch } from "react-redux"
import { Creators } from "model/apiWrapper"
import { useAccountAlias } from "resources/account"
import { useClassifications } from "resources/classifications"
import { useContentSetsMetadata } from "resources/content-sets"
import { useModelMetadata } from "resources/models"
import { useModelCurrencies } from "resources/currencies"
import { useModelCountries } from "resources/countries"
import { escapeContentSetId } from "helpers/contentSet"
import { OK, combineTanStackStatuses } from "helpers/data-statuses"
import { DashboardContext } from "connected-components/dashboard/context/dashboard"
import type { FactorRule } from "components/search-factor/types"
import type { Country } from "resources/countries"
import { ContentSetWithEscapedId, SimulationData, ContentSetRaw, FactorRuleDisplayType, PositionSetInput, FactorRuleDisplay, ModelMetadata, FilterOption, Classification } from "../types"
import { FACTOR_RULE_DISPLAY_TYPES } from "../constants"
import { buildRulesQuery, validateQueryParams } from "../query-builder"
import {
  transformFactorExposureToRules,
  transformContentSetFactorsToRules,
  transformBetasToRules,
  filterExistingRules,
  transformCompositionToRules,
} from "../helpers"
import { get } from "lodash"

interface GraphQLModelResponse {
  data: {
    model: {
      simulation: SimulationData | null
    } | null
  }
}

export type UseRulesResult = [FactorRule[], boolean]

function indexFactorsById<T extends { id: string }>(items: T[] | undefined): Record<string, T> {
  if (!items) return {}
  return Object.fromEntries(items.map(item => [item.id, item]))
}

function getExposureRules(
  rulesData: SimulationData,
  factorsMetadata: Record<string, NonNullable<ModelMetadata["factors"]>[number]>,
  contentSets: ContentSetWithEscapedId[] | null,
): FactorRule[] {
  return [
    ...transformFactorExposureToRules(rulesData.exposure, factorsMetadata),
    ...transformContentSetFactorsToRules(rulesData, contentSets),
  ]
}

function getCompositionRules(
  rulesData: SimulationData,
  metadata: {
    countries: Country[] | null
    currencies: FilterOption[] | undefined
    classifications: Classification[] | null
  },
): FactorRule[] {
  return [
    ...transformCompositionToRules(rulesData.composition, metadata),
    ...transformBetasToRules(rulesData.beta),
  ]
}

export function useRules(positionSet: PositionSetInput, ruleDisplayType: FactorRuleDisplayType, existingRules: FactorRuleDisplay[] = []): UseRulesResult {
  const { modelId, endDate } = useContext(DashboardContext)
  const dispatch = useDispatch()
  const account = useAccountAlias()

  const [contentSetsRaw, contentSetsStatus] = useContentSetsMetadata()
  const [classifications, classificationsStatus] = useClassifications()
  const [countries, countriesStatus] = useModelCountries(modelId)
  const [currencies, currenciesStatus] = useModelCurrencies(modelId)
  const [modelMetadata] = useModelMetadata(modelId)

  const contentSets = useMemo<ContentSetWithEscapedId[] | null>(() => {
    if (!contentSetsRaw) return null

    return contentSetsRaw.map((contentSet: ContentSetRaw): ContentSetWithEscapedId => ({
      ...contentSet,
      escapedId: escapeContentSetId(contentSet.id),
    }))
  }, [contentSetsRaw])

  const status = useMemo(() => combineTanStackStatuses([contentSetsStatus, classificationsStatus, countriesStatus, currenciesStatus]), [contentSetsStatus, classificationsStatus, countriesStatus, currenciesStatus])

  const query = useMemo(() => {
    if (status !== OK) {
      return null
    }

    if (!validateQueryParams(modelId, endDate, positionSet)) {
      return null
    }

    return buildRulesQuery(
      modelId,
      endDate,
      positionSet,
      contentSets,
      classifications,
      modelMetadata ?? undefined,
    )
  }, [
    modelId,
    endDate,
    positionSet,
    contentSets,
    classifications,
    modelMetadata,
    status,
  ])

  const result = useQuery({
    queryKey: ["optimize-rules", modelId, endDate, positionSet?.id],
    queryFn: () => new Promise<GraphQLModelResponse>((resolve, reject) => {
      dispatch(Creators.graphql(account, query?.query, query?.variables, resolve, reject))
    }),
    enabled: Boolean(query && modelId && endDate && positionSet),
    select: (value) => get(value, "data.model.simulation", null),
  })

  const rulesData = result.data || null
  const isLoading: boolean = result.isLoading

  const allRules = useMemo(() => {
    if (!rulesData) return []

    const factorsMetadata = indexFactorsById(modelMetadata?.factors)

    const rules = ruleDisplayType === FACTOR_RULE_DISPLAY_TYPES.EXPOSURE
      ? getExposureRules(rulesData, factorsMetadata, contentSets)
      : getCompositionRules(rulesData, { countries, currencies, classifications })

    return filterExistingRules(rules, existingRules)
  }, [
    rulesData,
    modelMetadata,
    contentSets,
    classifications,
    existingRules,
    ruleDisplayType,
    countries,
    currencies,
  ])

  return [
    allRules,
    isLoading,
  ]
}
